import { useQuery } from '@tanstack/react-query';
import { ExternalLink, MessageCircle, X } from 'lucide-react';
import { useState } from 'react';

import {
  clearShortcutFlag,
  COMMUNITY_CLOSED_KEY,
  COMMUNITY_MINIMIZED_KEY,
  readShortcutFlag,
  shortenUrl,
  writeShortcutFlag,
} from '@/lib/community-shortcut';
import { cn } from '@/lib/utils';
import { platformCommunityService } from '@/services/platform-community.service';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Ajakan bergabung ke grup WhatsApp pengguna SIPAUD — panel melayang di pojok
 * layar, hadir di setiap halaman dashboard lembaga.
 *
 * Tautannya dipasang Super Admin di konsol platform. TIDAK muncul selama
 * tautan itu kosong: panel yang menjanjikan grup lalu tidak membuka apa-apa
 * lebih buruk daripada tidak ada panel sama sekali.
 *
 * Punya TIGA keadaan, dan ketiganya disengaja:
 *
 * - terbuka — panel penuh, keadaan awal;
 * - mengecil — tinggal satu tombol bulat, bisa dibuka lagi kapan saja.
 *   Bertahan sepanjang sesi peramban, sebab panel yang kembali membentang tiap
 *   kali orang pindah menu adalah panel yang tidak pernah benar-benar bisa
 *   disingkirkan;
 * - tertutup — hilang sama sekali, bertahan lintas sesi.
 *
 * Keputusannya disimpan di peramban orang itu sendiri, bukan di server: ini
 * ajakan, bukan pengaturan lembaga, dan keputusan satu guru untuk
 * menyingkirkannya tidak boleh ikut menyingkirkannya dari layar rekan-rekannya.
 */
export function CommunityFab() {
  const user = useAuthStore((state) => state.user);
  const hasPermission = useAuthStore((state) => state.hasPermission);

  const [closedUrl, setClosedUrl] = useState<string | null>(() => readShortcutFlag('local', COMMUNITY_CLOSED_KEY));
  const [minimizedUrl, setMinimizedUrl] = useState<string | null>(() =>
    readShortcutFlag('session', COMMUNITY_MINIMIZED_KEY),
  );

  // Hanya pengguna lembaga. Super Admin dan pengguna Dinas tidak punya tenant,
  // dan grup pelanggan SIPAUD bukan untuk mereka — sekaligus mencegah request
  // yang pasti tidak berguna dari konsol platform.
  const isTenantUser = Boolean(user?.tenantId) && hasPermission('dashboard.read');

  const { data } = useQuery({
    queryKey: ['platform-community'],
    queryFn: () => platformCommunityService.getForTenant(),
    enabled: isTenantUser,
    // Praktis tidak pernah berubah, dan gagalnya tidak boleh berisik: ini
    // ajakan, bukan data lembaga. Halaman tetap utuh tanpa panel ini.
    staleTime: Infinity,
    retry: false,
  });

  const url = data?.whatsappGroupUrl;

  if (!url || closedUrl === url) {
    return null;
  }

  const isMinimized = minimizedUrl === url;

  const minimize = () => {
    writeShortcutFlag('session', COMMUNITY_MINIMIZED_KEY, url);
    setMinimizedUrl(url);
  };

  const expand = () => {
    clearShortcutFlag('session', COMMUNITY_MINIMIZED_KEY);
    setMinimizedUrl(null);
  };

  const closeForever = () => {
    writeShortcutFlag('local', COMMUNITY_CLOSED_KEY, url);
    setClosedUrl(url);
  };

  return (
    // `print:hidden` bukan kehati-hatian berlebih: aplikasi ini penuh halaman
    // cetak, dan panel melayang yang ikut tercetak di sudut lembar adalah cacat
    // yang baru ketahuan setelah kertasnya keluar.
    <div className="fixed bottom-6 right-6 z-40 print:hidden">
      {isMinimized ? (
        <button
          type="button"
          onClick={expand}
          title="Grup WhatsApp Pengguna SIPAUD"
          aria-label="Tampilkan ajakan grup WhatsApp SIPAUD"
          className={cn(
            'flex size-14 items-center justify-center rounded-full shadow-lg',
            'bg-[var(--color-primary)] text-[var(--color-primary-foreground)]',
            'transition-[box-shadow,opacity] hover:opacity-95 hover:shadow-xl',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
            'focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-background)]',
          )}
        >
          <MessageCircle className="size-6" aria-hidden="true" />
        </button>
      ) : (
        <div
          className={cn(
            'w-[19rem] max-w-[calc(100vw-3rem)] overflow-hidden rounded-[var(--radius-lg)]',
            'border border-[var(--color-border)] bg-[var(--color-card)] shadow-xl',
          )}
        >
          <div className="flex items-start gap-3 bg-[var(--color-primary)] p-4 text-[var(--color-primary-foreground)]">
            <MessageCircle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight">Link dari SIPAUD.com</p>
              <p className="mt-0.5 text-xs opacity-90">Bergabung bersama kami</p>
            </div>

            {/* Silang di kepala panel MENGECILKAN, tidak menutup permanen.
                Yang menekan silang biasanya sedang menyingkirkan penghalang
                sesaat, bukan memutuskan tak ingin diberi tahu lagi — dan
                menafsirkannya sebagai keputusan besar berarti mengambil
                keputusan itu atas nama orangnya. Yang permanen ada di kaki
                panel, bertuliskan apa adanya. */}
            <button
              type="button"
              onClick={minimize}
              aria-label="Sembunyikan sementara"
              className={cn(
                'shrink-0 rounded-[var(--radius-sm)] p-0.5 opacity-80 transition-opacity',
                'hover:opacity-100 focus-visible:outline-none focus-visible:ring-2',
                'focus-visible:ring-[var(--color-primary-foreground)]',
              )}
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          </div>

          <div className="p-3">
            {/* Elemen <a>, bukan tombol ber-onClick: pengguna bisa membukanya
                di tab baru, menyalin alamatnya, atau membukanya di ponsel —
                tiga hal yang semuanya hilang bila navigasinya dikerjakan
                JavaScript. */}
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className={cn(
                'flex items-center gap-3 rounded-[var(--radius-md)] border p-3 transition-colors',
                'border-[var(--color-border)] hover:border-[var(--color-primary)]/40',
                'hover:bg-[var(--color-primary)]/5',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
              )}
            >
              <span
                className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--color-success-subtle)] text-[var(--color-success-on-subtle)]"
                aria-hidden="true"
              >
                <MessageCircle className="size-5" />
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  Gabung Grup WhatsApp SIPAUD
                </span>
                {/* Alamatnya ditampilkan, bukan disembunyikan di balik teks
                    ajakan: tautan keluar yang tujuannya tidak terbaca adalah
                    hal yang justru kami minta orang waspadai. */}
                <span className="block truncate text-xs text-[var(--color-muted-foreground)]">
                  {shortenUrl(url)}
                </span>
              </span>

              <ExternalLink
                className="size-4 shrink-0 text-[var(--color-muted-foreground)]"
                aria-hidden="true"
              />
            </a>
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-[var(--color-border)] px-3 py-2">
            <button
              type="button"
              onClick={minimize}
              className="rounded-[var(--radius-sm)] px-1 py-0.5 text-xs text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
            >
              Sembunyikan sementara
            </button>

            <button
              type="button"
              onClick={closeForever}
              className="rounded-[var(--radius-sm)] px-1 py-0.5 text-xs font-medium text-[var(--color-destructive)] transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
            >
              Jangan tampilkan lagi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
